import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { toast } from "sonner";

interface TransferItemParams {
  orderItemId: string;
  fromOrderId: string;
  toOrderId: string;
  quantity: number;
}

export function useItemTransfer() {
  const { activeRestaurant } = useRestaurantContext();
  const [isTransferring, setIsTransferring] = useState(false);

  const transferItem = async (params: TransferItemParams) => {
    if (!activeRestaurant) return false;

    setIsTransferring(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) throw new Error("User not authenticated");

      // Get the original item
      const { data: originalItem, error: itemError } = await supabase
        .from("order_items")
        .select("*")
        .eq("id", params.orderItemId)
        .single();

      if (itemError) throw itemError;

      if (params.quantity >= originalItem.quantity) {
        // Move the entire item
        const { error: updateError } = await supabase
          .from("order_items")
          .update({ order_id: params.toOrderId })
          .eq("id", params.orderItemId);

        if (updateError) throw updateError;
      } else {
        // Split the item
        // Reduce quantity on original
        const { error: reduceError } = await supabase
          .from("order_items")
          .update({
            quantity: originalItem.quantity - params.quantity,
            total_price: originalItem.unit_price * (originalItem.quantity - params.quantity),
          })
          .eq("id", params.orderItemId);

        if (reduceError) throw reduceError;

        // Create new item on destination
        const { error: createError } = await supabase.from("order_items").insert({
          order_id: params.toOrderId,
          product_id: originalItem.product_id,
          product_name: originalItem.product_name,
          quantity: params.quantity,
          unit_price: originalItem.unit_price,
          total_price: originalItem.unit_price * params.quantity,
          notes: originalItem.notes,
        });

        if (createError) throw createError;
      }

      // Log the transfer
      await supabase.from("item_transfers").insert({
        restaurant_id: activeRestaurant.id,
        order_item_id: params.orderItemId,
        from_order_id: params.fromOrderId,
        to_order_id: params.toOrderId,
        quantity: params.quantity,
        transferred_by: user.id,
      });

      // Update order totals
      await updateOrderTotals(params.fromOrderId);
      await updateOrderTotals(params.toOrderId);

      toast.success("Item transferido com sucesso!");
      return true;
    } catch (error) {
      console.error("Error transferring item:", error);
      toast.error("Erro ao transferir item");
      return false;
    } finally {
      setIsTransferring(false);
    }
  };

  const updateOrderTotals = async (orderId: string) => {
    const { data: items, error } = await supabase
      .from("order_items")
      .select("total_price")
      .eq("order_id", orderId);

    if (error) {
      console.error("Error fetching items for total:", error);
      return;
    }

    const subtotal = items.reduce((sum, item) => sum + Number(item.total_price), 0);

    await supabase
      .from("orders")
      .update({ subtotal, total: subtotal })
      .eq("id", orderId);
  };

  return {
    transferItem,
    isTransferring,
  };
}
